/**
 * VueUseのHTTP統合ライブラリ
 * - useAxios: AxiosベースのHTTPリクエストをVue Composition APIで使用
 * - StrictUseAxiosReturn: useAxiosの戻り値の厳密な型定義
 */
import { StrictUseAxiosReturn, useAxios } from "@vueuse/integrations/useAxios";

/**
 * APIルーティング定数
 * - エンドポイントのURLパスを一元管理
 */
import ApiRouterConstants from "../constants/api.router.constants";

/**
 * 予約関連の型定義
 * - APIリクエスト/レスポンスの型
 * - 予約データのインターフェース
 * - 各種操作に必要なパラメータの型
 */
import {
  ApiBookingIndexReq,
  ApiBookingShowReq,
  ApiBookingIndexRes,
  IBooking,
  ApiCommentBookingReq,
  ApiBookingSaveReq,
  ApiSaveStatusBookingReq,
  ApiBookingExportReq,
  ApiListBookingHistoryReq,
} from "../typings";

/**
 * Axios HTTPライブラリ
 * - AxiosResponse: HTTPレスポンスの型定義
 */
import { AxiosResponse } from "axios";

/**
 * 共通HTTPユーティリティ
 * - インターセプター、ベースURL、認証などの設定
 */
import http from "@/common/utils/http-common-utils";

/**
 * VueUseユーティリティライブラリ
 * - AnyFn: 任意の関数の型定義（コールバック関数用）
 */
import { AnyFn } from "@vueuse/core";

/**
 * 予約関連のAPIサービス
 * 予約の取得、作成、更新、削除、ステータス変更、複製などの操作を提供
 */
const BookingServices = {
  /**
   * 予約一覧を取得する
   * @param {ApiBookingIndexReq} params - 予約一覧取得のリクエストパラメータ
   * @returns {Promise<StrictUseAxiosReturn<AxiosResponse<ApiBookingIndexRes>, AxiosResponse, ApiBookingIndexRes>>} 予約一覧のレスポンス
   */
  getBookings: async (
    params: ApiBookingIndexReq
  ): Promise<StrictUseAxiosReturn<AxiosResponse<ApiBookingIndexRes>, AxiosResponse, ApiBookingIndexRes>> => {
    const { execute } = useAxios<AxiosResponse<ApiBookingIndexRes>>(
      ApiRouterConstants.API_BOOKINGS,
      { method: "GET", params },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },

  /**
   * 予約履歴一覧を取得する
   * @param {ApiListBookingHistoryReq} params - 予約履歴一覧取得のリクエストパラメータ
   * @returns {Promise<StrictUseAxiosReturn<AxiosResponse<ApiBookingIndexRes>, AxiosResponse, ApiBookingIndexRes>>} 予約履歴一覧のレスポンス
   */
  listBookings: async (
    params: ApiListBookingHistoryReq
  ): Promise<StrictUseAxiosReturn<AxiosResponse<ApiBookingIndexRes>, AxiosResponse, ApiBookingIndexRes>> => {
    const { execute } = useAxios<AxiosResponse<ApiBookingIndexRes>>(
      `${ApiRouterConstants.API_BOOKINGS}/list`,
      { method: "GET", params },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },

  /**
   * 指定されたIDの予約詳細を取得する
   * @param {ApiBookingShowReq} params - 予約詳細取得のリクエストパラメータ（IDを含む）
   * @returns {Promise<StrictUseAxiosReturn<AxiosResponse<IBooking>, AxiosResponse, IBooking>>} 予約詳細のレスポンス
   */
  getBooking: async (
    params: ApiBookingShowReq
  ): Promise<StrictUseAxiosReturn<AxiosResponse<IBooking>, AxiosResponse, IBooking>> => {
    const { id } = params;
    const { execute } = useAxios<AxiosResponse<IBooking>>(
      `${ApiRouterConstants.API_BOOKINGS}/${id}`,
      { method: "GET", params },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },

  /**
   * 予約にコメントを保存する
   * @param {ApiBookingShowReq} params - 予約識別のリクエストパラメータ（IDを含む）
   * @param {ApiCommentBookingReq} payload - 保存するコメントのデータ
   * @returns {Promise<void>} 処理完了を示すPromise
   */
  saveComment: async (params: ApiBookingShowReq, payload: ApiCommentBookingReq): Promise<void> => {
    const { id } = params;
    const { execute } = useAxios<AxiosResponse<IBooking>>(
      `${ApiRouterConstants.API_BOOKINGS}/${id}/comments`,
      { method: "PATCH", params, data: payload },
      http,
      { immediate: false }
    );
    await execute();
  },
  /**
   * 予約を保存する（新規作成または更新）
   * @param {ApiBookingShowReq} params - 予約識別のリクエストパラメータ（IDを含む、新規の場合は空）
   * @param {ApiBookingSaveReq} payload - 保存する予約データ
   * @param {AnyFn} onSuccess - 成功時のコールバック関数
   * @param {AnyFn} onError - エラー時のコールバック関数
   * @returns {Promise<void>} 処理完了を示すPromise
   */
  saveBooking: async (
    params: ApiBookingShowReq,
    payload: ApiBookingSaveReq,
    onSuccess: AnyFn,
    onError: AnyFn
  ): Promise<void> => {
    const { id } = params;
    const [idUrl, method] = !id ? ["", "POST"] : [`/${id}`, "PUT"];
    const { execute } = useAxios<AxiosResponse<IBooking>>(
      `${ApiRouterConstants.API_BOOKINGS}${idUrl}`,
      { method, params, data: payload },
      http,
      {
        immediate: false,
        onSuccess,
        onError,
      }
    );
    await execute();
  },

  /**
   * 予約のステータスを変更する
   * @param {ApiBookingShowReq} params - 予約識別のリクエストパラメータ（IDを含む）
   * @param {ApiSaveStatusBookingReq} payload - 変更するステータスデータ
   * @param {AnyFn} onSuccess - 成功時のコールバック関数
   * @param {AnyFn} onError - エラー時のコールバック関数
   * @returns {Promise<void>} 処理完了を示すPromise
   */
  saveStatus: async (
    params: ApiBookingShowReq,
    payload: ApiSaveStatusBookingReq,
    onSuccess: AnyFn,
    onError: AnyFn
  ): Promise<void> => {
    const { id } = params;
    const { execute } = useAxios<AxiosResponse<IBooking>>(
      `${ApiRouterConstants.API_BOOKINGS}/${id}/change_status`,
      { method: "PATCH", params, data: payload },
      http,
      {
        immediate: false,
        onSuccess,
        onError,
      }
    );
    await execute();
  },

  /**
   * 予約データをエクスポートする
   * @param {ApiBookingExportReq} payload - エクスポートのリクエストパラメータ
   * @returns {Promise<any>} エクスポート処理の結果
   */
  exportBookings: async (payload: ApiBookingExportReq) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_BOOKINGS}/export`,
      {
        method: "GET",
      },
      http,
      {
        immediate: false,
      }
    );
    return await execute({ params: payload });
  },

  /**
   * 予約を削除する
   * @param {number} bookingId - 削除する予約のID
   * @param {number} clinicId - クリニックID
   * @param {string} resource - リソース識別子
   * @param {AnyFn} onSuccess - 成功時のコールバック関数
   * @param {AnyFn} onError - エラー時のコールバック関数
   * @returns {Promise<any>} 削除処理の結果
   */
  deleteBooking: async (bookingId: number, clinicId: number, resource: string, onSuccess: AnyFn, onError: AnyFn) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_BOOKINGS}/${bookingId}?clinic_id=${clinicId}&resource=${resource}`,
      {
        method: "DELETE",
      },
      http,
      {
        immediate: false,
        onSuccess,
        onError,
      }
    );
    return await execute();
  },

  /**
   * 予約リクエストを承認する
   * @param {number} bookingId - 承認する予約のID
   * @param {number} requestId - リクエストID
   * @param {number} clinicId - クリニックID
   * @param {AnyFn} onSuccess - 成功時のコールバック関数
   * @param {AnyFn} onError - エラー時のコールバック関数
   * @returns {Promise<any>} 承認処理の結果
   */
  approveRequest: async (bookingId: number, requestId: number, clinicId: number, onSuccess: AnyFn, onError: AnyFn) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_REQUEST_BOOKINGS}/${bookingId}/approve?clinic_id=${clinicId}`,
      {
        method: "PATCH",
      },
      http,
      {
        immediate: false,
        onSuccess,
        onError,
      }
    );
    return await execute({ params: { request_id: requestId } });
  },

  /**
   * バッチで予約を保存する（新規作成または更新）
   * @param {ApiBookingShowReq} params - 予約識別のリクエストパラメータ（IDを含む、新規の場合は空）
   * @param {ApiBookingSaveReq} payload - 保存する予約データ
   * @param {AnyFn} onSuccess - 成功時のコールバック関数
   * @param {AnyFn} onError - エラー時のコールバック関数
   * @returns {Promise<void>} 処理完了を示すPromise
   */
  saveBatchBooking: async (
    params: ApiBookingShowReq,
    payload: ApiBookingSaveReq,
    onSuccess: AnyFn,
    onError: AnyFn
  ): Promise<void> => {
    const { id } = params;
    const [idUrl, method] = !id ? ["/batch_create", "POST"] : ["/batch_update", "PUT"];
    const { execute } = useAxios<AxiosResponse<IBooking>>(
      `${ApiRouterConstants.API_BOOKINGS}${idUrl}`,
      { method, params, data: payload },
      http,
      {
        immediate: false,
        onSuccess,
        onError,
      }
    );
    await execute();
  },

  /**
   * バッチで予約のステータスを変更する
   * @param {any} params - 予約識別のリクエストパラメータ（IDを含む）
   * @param {ApiSaveStatusBookingReq} payload - 変更するステータスデータ
   * @param {AnyFn} onSuccess - 成功時のコールバック関数
   * @param {AnyFn} onError - エラー時のコールバック関数
   * @returns {Promise<void>} 処理完了を示すPromise
   */
  saveBatchStatus: async (
    params: any,
    payload: ApiSaveStatusBookingReq,
    onSuccess: AnyFn,
    onError: AnyFn
  ): Promise<void> => {
    const { id } = params;
    const { execute } = useAxios<AxiosResponse<IBooking>>(
      `${ApiRouterConstants.API_BOOKINGS}/${id}/batch_change_status`,
      { method: "PATCH", params, data: payload },
      http,
      {
        immediate: false,
        onSuccess,
        onError,
      }
    );
    await execute();
  },

  /**
   * HotPepperの確認を実行する
   * @param {ApiBookingShowReq} params - 予約識別のリクエストパラメータ（IDを含む）
   * @param {AnyFn} onSuccess - 成功時のコールバック関数
   * @returns {Promise<void>} 処理完了を示すPromise
   */
  confirmHotpepper: async (params: ApiBookingShowReq, onSuccess: AnyFn): Promise<void> => {
    const { id } = params;
    const { execute } = useAxios<AxiosResponse<IBooking>>(
      `${ApiRouterConstants.API_BOOKINGS}/${id}/hotpepper/confirmations`,
      { method: "PATCH", params },
      http,
      {
        immediate: false,
        onSuccess,
      }
    );
    await execute();
  },

  /**
   * 予約を複製する
   * @param {ApiBookingShowReq} params - 予約識別のリクエストパラメータ（IDを含む）
   * @param {ApiBookingSaveReq} payload - 複製する予約データ
   * @param {AnyFn} onSuccess - 成功時のコールバック関数
   * @param {AnyFn} onError - エラー時のコールバック関数
   * @returns {Promise<void>} 処理完了を示すPromise
   */
  duplicateBooking: async (
    params: ApiBookingShowReq,
    payload: ApiBookingSaveReq,
    onSuccess: AnyFn,
    onError: AnyFn
  ): Promise<void> => {
    const { id } = params;

    const { execute } = useAxios<AxiosResponse<IBooking>>(
      `${ApiRouterConstants.API_BOOKINGS}/${id}/duplicate`,
      { method: "POST", params, data: payload },
      http,
      {
        immediate: false,
        onSuccess,
        onError,
      }
    );
    await execute();
  },
};

export default BookingServices;
